import * as React from 'react';

export interface CountUpProps {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  format?: boolean;
  className?: string;
}

export function CountUp({
  end,
  duration = 1100,
  prefix = '',
  suffix = '',
  decimals = 0,
  format = true,
  className,
}: CountUpProps) {
  const [val, setVal] = React.useState(0);
  const reduceMotion = React.useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia('(prefers-reduced-motion: reduce)');
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );

  React.useEffect(() => {
    if (reduceMotion) {
      setVal(end);
      return;
    }
    let raf = 0;
    let start: number | null = null;
    const step = (ts: number) => {
      if (start == null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(end * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, reduceMotion]);

  const formatted = format
    ? val.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : val.toFixed(decimals);

  return (
    <span className={className} aria-label={`${prefix}${end}${suffix}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
